import React, { useMemo, useCallback } from 'react';
import { NavHashLink } from 'react-router-hash-link';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ReactComponent as ChevronRightIcon } from 'assets/images/svg/chevron-right.svg';
import { ReactComponent as HomeIcon } from 'assets/images/svg/home.svg';
import { ReactComponent as CalendarIcon } from 'assets/images/svg/calendar-icon.svg';
import { ReactComponent as GearIcon } from 'assets/images/svg/gear.svg';
import { ReactComponent as GpsIcon } from 'assets/images/svg/gps.svg';
import { ReactComponent as HeartOutlineIcon } from 'assets/images/svg/heart-outline.svg';
import { ReactComponent as SpecialsIcon } from 'assets/svg/specials.svg';
import { ReactComponent as LocationIcon } from 'assets/images/svg/location.svg';
import { ReactComponent as OffersIcon } from 'assets/svg/offers-icon.svg';
import { ReactComponent as NationalMembershipIcon } from 'assets/svg/national-membership.svg';
import { ReactComponent as PaymentsIcon } from 'assets/images/svg/payments.svg';
import { ReactComponent as StoreIcon } from 'assets/images/svg/icons/store-icon.svg';

import container from 'containers/HeaderMenuContainer';
import { NavLinkWithPreload } from 'components/shared/PreloadableLinks';

import { features } from 'utils/features';
import { UPCOMING } from 'utils/bookingStatus';
import { CURRENT_LOCATION } from 'utils/constants';
import {
  ACCOUNT_SETTINGS,
  MY_MEMBERSHIP,
  NETWORK_MEMBERSHIP_SALES,
  NETWORK_OFFERS,
  BOOKINGS,
  COMMUNICATION_PREFERENCES,
  EXPLORE_PREFIX,
  FAVORITES,
  FAQS,
  FREE_MARKETPLACE,
  HOME,
  MY_PROFILE,
  PAYMENT_METHODS,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
  VIP_PERKS,
  CANCELLATION_POLICY,
} from 'utils/routes';
import { M } from 'utils/siteStyle';

const HeaderMenuList = ({
  toggleMenu,
  storeUrl,
  setLocation,
  setLocationFilter,
  setNearMeSearch,
  performSearch,
  location,
  setHotDealsSearch,
}) => {
  const status = useSelector(({ site }) => site?.data);
  const membershipName = M.BT ? 'Golf Time Club' : 'SG Club';

  const selectDealsSearch = useCallback(() => {
    toggleMenu();
    setLocationFilter({
      ...CURRENT_LOCATION,
      type: 'City',
      hierarchizedUrl: location.hierarchizedUrl,
      latitude: location.lat,
      longitude: location.lon,
    });
    setHotDealsSearch();
    performSearch('City');
  }, [setLocationFilter, setHotDealsSearch, performSearch,
    location.lat, location.lon, location.hierarchizedUrl, toggleMenu,
  ]);

  const renderNearMeLink = useMemo(() => (
    location && location.city && location.state ? (
      <button
        id="navbar-menu-near-me"
        className="flex items-center gap-4 cursor-pointer focus:outline-none"
        onClick={() => {
          toggleMenu();
          setLocationFilter({
            ...CURRENT_LOCATION,
            latitude: location.lat,
            longitude: location.lon,
            hierarchizedUrl: location.hierarchizedUrl,
          });
          setNearMeSearch();
          performSearch();
        }}
      >
        <div><GpsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
        <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Near me</div>
      </button>
    ) : (
      <NavHashLink
        id="navbar-menu-near-me"
        to="/#courses-near-me"
        onClick={() => {
          toggleMenu();
        }}
        smooth
      >
        <div className="flex items-center gap-4">
          <div><GpsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Near me</div>
        </div>
      </NavHashLink>
    )
  ), [location, performSearch, setLocationFilter, setNearMeSearch, toggleMenu]);

  const renderDealsLink = useMemo(() => {
    if (location && location.city && location.state) {
      return (
        <button
          id="navbar-menu-deals"
          className="flex items-center gap-4 cursor-pointer focus:outline-none"
          onClick={selectDealsSearch}
        >
          <div><SpecialsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Specials</div>
        </button>
      );
    }
    return (
      <NavHashLink
        id="navbar-menu-deals"
        to="/#courses-near-me"
        onClick={() => {
          toggleMenu();
        }}
      >
        <div className="flex items-center gap-4">
          <div><SpecialsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
          <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Specials</div>
        </div>
      </NavHashLink>
    );
  }, [location, selectDealsSearch, toggleMenu]);

  return (
    <div className="overflow-auto pt-4-5">
      <div className="px-6">
        <h1 className="pb-10 leading-4 font-primary text-4 -tracking-0.2 text-dark">{process.env.REACT_APP_SHORT_NAME}</h1>
        <div className="flex flex-col gap-10 px-4">
          <NavLinkWithPreload
            to={HOME}
            onClick={() => {
              toggleMenu();
              setLocation({});
            }}
          >
            <div className="flex items-center gap-4">
              <div><HomeIcon className="w-4 h-4 fill-current text-grey-3" /></div>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Home</div>
            </div>
          </NavLinkWithPreload>
          {features.storeUrl && storeUrl && (
            <a id="navbar-menu-store" href={storeUrl} target="_blank" rel="noopener noreferrer" onClick={toggleMenu}>
              <div className="flex items-center gap-4">
                <div><StoreIcon className="w-4 h-4 fill-current text-grey-3" /></div>
                <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Shop</div>
              </div>
            </a>
          )}
          <NavLinkWithPreload to={BOOKINGS.replace(':status', UPCOMING)} onClick={toggleMenu}>
            <div id="navbar-menu-bookings" className="flex items-center gap-4">
              <div><CalendarIcon className="w-4 h-4 fill-current text-grey-3" /></div>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Bookings</div>
            </div>
          </NavLinkWithPreload>
          <NavLinkWithPreload to={FAVORITES} onClick={toggleMenu}>
            <div id="navbar-menu-favorites" className="flex items-center gap-4">
              <div><HeartOutlineIcon className="w-4 h-4 fill-current text-grey-3" /></div>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Favorites</div>
            </div>
          </NavLinkWithPreload>
          <NavLinkWithPreload to={PAYMENT_METHODS} onClick={toggleMenu}>
            <div id="navbar-menu-payment-methods" className="flex items-center gap-4">
              <div><PaymentsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Payments</div>
            </div>
          </NavLinkWithPreload>
          <NavLinkWithPreload to={MY_PROFILE} onClick={toggleMenu}>
            <div id="navbar-menu-my-profile" className="flex items-center gap-4">
              <svg className="w-4 h-4 fill-current stroke-current text-grey-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <g fill="none" fillRule="evenodd" transform="translate(1 1)">
                  <circle cx="11" cy="11" r="11" stroke="inherit" strokeWidth="1.5" />
                </g>
                <path fill="inherit" fillRule="nonzero" strokeWidth="0" d="M11.2 15c4.69 0 7.2 1.597 7.2 4.8a.6.6 0 0 1-1.2 0c0-2.397-1.89-3.6-6-3.6s-6 1.203-6 3.6a.6.6 0 1 1-1.2 0c0-3.203 2.51-4.8 7.2-4.8zm0-9a4.2 4.2 0 1 1 0 8.4 4.2 4.2 0 0 1 0-8.4zm0 1.238a2.962 2.962 0 1 0 0 5.924 2.962 2.962 0 0 0 0-5.924z" transform="translate(1 1)" />
              </svg>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">My Profile</div>
            </div>
          </NavLinkWithPreload>
          {features.networkMembership && status?.networkMembershipEnabled && (
            <>
              <NavLinkWithPreload to={MY_MEMBERSHIP} onClick={toggleMenu}>
                <div id="navbar-menu-my-membership" className="flex items-center gap-4">
                  <NationalMembershipIcon className="w-4 h-4 text-grey-3 fill-current" />
                  <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">{membershipName}</div>
                </div>
              </NavLinkWithPreload>
              <NavLinkWithPreload to={NETWORK_OFFERS.replace(':status', 'upcoming')} onClick={toggleMenu}>
                <div id="navbar-menu-my-offers" className="flex items-center gap-4">
                  <OffersIcon className="w-4 h-4 text-grey-3 fill-current" />
                  <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">My Offers</div>
                </div>
              </NavLinkWithPreload>
            </>
          )}
          <NavLinkWithPreload to={ACCOUNT_SETTINGS} onClick={toggleMenu}>
            <div id="navbar-menu-account-settings" className="flex items-center gap-4">
              <div><GearIcon className="w-4 h-4 fill-current text-grey-3" /></div>
              <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Settings</div>
            </div>
          </NavLinkWithPreload>
        </div>
      </div>
      {(features.nearMeNav
      || features.topDeals
      || features.exploreCountriesStates
      || features.giftCardLink
      || features.membership
      || features.rewardsAccumulation) && (
        <>
          <div className="w-full h-px my-10 bg-pale-grey" />
          <div className="px-6">
            <h1 className="pb-10 leading-4 font-primary text-4 -tracking-0.2 text-dark">Everything Else</h1>
            <div className="flex flex-col gap-10 px-4">
              {features.nearMeNav && renderNearMeLink}
              {features.topDeals && renderDealsLink}
              {features.exploreCountriesStates && (
                <NavLinkWithPreload to={`${EXPLORE_PREFIX}/united-states`} onClick={toggleMenu}>
                  <div id="navbar-menu-explore" className="flex items-center gap-4">
                    <div><LocationIcon className="w-4 h-4 fill-current text-grey-3" /></div>
                    <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Explore</div>
                  </div>
                </NavLinkWithPreload>
              )}
              {features.giftCardLink && (
                <a id="navbar-menu-gift-cards" href="https://info.supremegolf.com/supreme-golf-gift-cards/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4">
                  <div><PaymentsIcon className="w-4 h-4 fill-current text-grey-3" /></div>
                  <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Gift Cards</div>
                </a>
              )}
              {features.networkMembership && status?.networkMembershipEnabled && (
                <NavLinkWithPreload to={NETWORK_MEMBERSHIP_SALES} onClick={toggleMenu}>
                  <div id="navbar-menu-membership" className="flex items-center gap-4">
                    <NationalMembershipIcon className="w-4 h-4 text-grey-3 fill-current" />
                    <div className="font-bold leading-5 font-primary text-4 text-dark -tracking-0.6">Club</div>
                  </div>
                </NavLinkWithPreload>
              )}
            </div>
          </div>
        </>
      )}
      <div className="flex flex-col gap-10 px-6 py-10 mt-10 bg-white-two">
        {process.env.REACT_APP_NAME === 'supremeGolf' && (
          <a
            href="https://courses.supremegolf.com/tee-time-distribution/?utm_source=supremegolf.com&utm_medium=referral&utm_campaign=homepage"
            target="_blank"
            rel="noopener noreferrer"
            onClick={toggleMenu}
          >
            <div className="flex items-center justify-between">
              <div className="leading-4 text-4 -tracking-0.2 text-dark">List My Course</div>
              <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
            </div>
          </a>
        )}
        {process.env.REACT_APP_NAME === 'supremeGolf' && (
          <NavLinkWithPreload to={COMMUNICATION_PREFERENCES} onClick={toggleMenu}>
            <div className="flex items-center justify-between">
              <div className="leading-4 text-4 -tracking-0.2 text-dark">Supreme Golf Newsletter</div>
              <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
            </div>
          </NavLinkWithPreload>
        )}
        {features.courseOperators && (
          <>
            {features.courseOperatorsFreeMarketplace && (
              <NavLinkWithPreload to={FREE_MARKETPLACE} onClick={toggleMenu}>
                <div className="flex items-center justify-between">
                  <div className="leading-4 text-4 -tracking-0.2 text-dark">List My Course</div>
                  <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
                </div>
              </NavLinkWithPreload>
            )}
            {features.courseOperatorsVipPerks && (
              <NavLinkWithPreload to={VIP_PERKS} onClick={toggleMenu}>
                <div className="flex items-center justify-between">
                  <div className="leading-4 text-4 -tracking-0.2 text-dark">VIP Perks</div>
                  <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
                </div>
              </NavLinkWithPreload>
            )}
          </>
        )}
        {process.env.REACT_APP_NAME === 'barstool' && (
          <NavLinkWithPreload to={COMMUNICATION_PREFERENCES} onClick={toggleMenu}>
            <div className="flex items-center justify-between">
              <div className="leading-4 text-4 -tracking-0.2 text-dark">Barstool Golftime Newsletter</div>
              <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
            </div>
          </NavLinkWithPreload>
        )}
        <NavLinkWithPreload to={CANCELLATION_POLICY} onClick={toggleMenu}>
          <div className="flex items-center justify-between">
            <div className="leading-4 text-4 -tracking-0.2 text-dark">Cancellation Options</div>
            <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
          </div>
        </NavLinkWithPreload>
        <NavLinkWithPreload to={FAQS} onClick={toggleMenu}>
          <div className="flex items-center justify-between">
            <div className="leading-4 text-4 -tracking-0.2 text-dark">Help Center</div>
            <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
          </div>
        </NavLinkWithPreload>
        <NavLinkWithPreload to={PRIVACY_POLICY} onClick={toggleMenu}>
          <div className="flex items-center justify-between">
            <div className="leading-4 text-4 -tracking-0.2 text-dark">Privacy Policy</div>
            <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
          </div>
        </NavLinkWithPreload>
        <NavLinkWithPreload to={TERMS_AND_CONDITIONS} onClick={toggleMenu}>
          <div className="flex items-center justify-between">
            <div className="leading-4 text-4 -tracking-0.2 text-dark">Terms of Service</div>
            <div><ChevronRightIcon className="w-3 h-3 text-black fill-current" /></div>
          </div>
        </NavLinkWithPreload>
      </div>
    </div>
  );
};

HeaderMenuList.defaultProps = {
  storeUrl: null,
  toggleMenu: null,
  setHotDealsSearch: null,
};

HeaderMenuList.propTypes = {
  location: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    hierarchizedUrl: PropTypes.string,
  }).isRequired,
  performSearch: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
  setLocationFilter: PropTypes.func.isRequired,
  setNearMeSearch: PropTypes.func.isRequired,
  storeUrl: PropTypes.string,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      query: PropTypes.shape({
        date: PropTypes.string,
      }),
    }),
  }).isRequired,
  city: PropTypes.shape({
    isLoading: PropTypes.bool,
    city: PropTypes.shape({
      id: PropTypes.number,
      fullName: PropTypes.string,
    }),
  }).isRequired,
  course: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  toggleMenu: PropTypes.func,
  setHotDealsSearch: PropTypes.func,
};

export default container(HeaderMenuList);
